import type { PaneState } from "../state.js";
import type {
  BenchmarkMode,
  PaneMode,
  TimelineEvent,
} from "../types.js";
import { toPaneMode } from "../types.js";
import {
  formatDuration,
  formatElapsed,
  shortActor,
  toolIcon,
} from "../utils/format.js";
import {
  iconChat,
  iconCheck,
  iconCross,
  iconError,
  iconFinish,
  iconFork,
  iconInfo,
  iconPrompt,
  iconStart,
} from "../utils/icons.js";
import {
  escapeHtml,
  formatArgsHuman,
  formatJson,
  toggleRawView,
} from "./dom-helpers.js";

const collapsedSubagentGroups = new Set<string>();
const collapsedPromptEvents = new Set<string>();

type AnimationConfig = {
  duration: string;
  translate: string;
};

let animConfig: AnimationConfig = { duration: "0.35s", translate: "8px" };

function resolvePaneMode(mode: BenchmarkMode | PaneMode): PaneMode {
  return toPaneMode(mode as BenchmarkMode);
}

function applyNodeAnimation(el: HTMLElement, staggerIndex = -1) {
  el.style.setProperty("--node-anim-duration", animConfig.duration);
  el.style.setProperty("--node-anim-translate", animConfig.translate);
  if (staggerIndex > 0) {
    const delayMs = Math.min(staggerIndex * 20, 600);
    el.style.setProperty("--node-anim-delay", `${delayMs}ms`);
  }
}

export function setReplayAnimationSpeed(speed: number) {
  if (speed <= 0) {
    animConfig = { duration: "0.35s", translate: "8px" };
  } else if (speed <= 2) {
    animConfig = { duration: "0.28s", translate: "5px" };
  } else if (speed <= 8) {
    animConfig = { duration: "0.18s", translate: "3px" };
  } else {
    animConfig = { duration: "0.12s", translate: "2px" };
  }
  document.documentElement.style.setProperty("--node-anim-duration", animConfig.duration);
  document.documentElement.style.setProperty("--node-anim-translate", animConfig.translate);
}

export function updatePaneTimer(mode: BenchmarkMode | PaneMode, pane: PaneState, phase: string) {
  const paneMode = resolvePaneMode(mode);
  const elapsedEl = document.getElementById(`elapsed-${paneMode}`)!;
  const dotEl = document.getElementById(`status-dot-${paneMode}`)!;
  elapsedEl.textContent = pane.elapsed > 0 ? formatElapsed(pane.elapsed) : "--:--";

  if (phase === "running") {
    dotEl.className = "status-dot status-dot--running";
  } else if (phase === "done") {
    dotEl.className = "status-dot status-dot--online";
  } else {
    dotEl.className = "status-dot";
  }
}

export function showTimeline(mode: BenchmarkMode | PaneMode) {
  const paneMode = resolvePaneMode(mode);
  document.getElementById(`idle-${paneMode}`)!.style.display = "none";
  document.getElementById(`timeline-${paneMode}`)!.style.display = "block";
}

export function resetPaneUI(mode: BenchmarkMode | PaneMode) {
  const paneMode = resolvePaneMode(mode);
  document.getElementById(`idle-${paneMode}`)!.style.display = "";
  const timeline = document.getElementById(`timeline-${paneMode}`)!;
  timeline.style.display = "none";
  timeline.innerHTML = "";
  collapsedSubagentGroups.clear();
  collapsedPromptEvents.clear();
  const dotEl = document.getElementById(`status-dot-${paneMode}`)!;
  dotEl.className = "status-dot";
  document.getElementById(`elapsed-${paneMode}`)!.textContent = "--:--";
}

export function appendEventNode(mode: BenchmarkMode | PaneMode, event: TimelineEvent) {
  const paneMode = resolvePaneMode(mode);
  const timeline = document.getElementById(`timeline-${paneMode}`)!;
  appendEventNodeToTimeline(timeline, paneMode, event);
}

export function upsertEventNode(mode: BenchmarkMode | PaneMode, event: TimelineEvent) {
  const paneMode = resolvePaneMode(mode);
  const timeline = document.getElementById(`timeline-${paneMode}`)!;
  const existingNode = timeline.querySelector(
    `[data-event-id="${event.id}"]`,
  ) as HTMLElement | null;
  const nextNode = createEventNode(event, paneMode);
  if (!nextNode) {
    return;
  }

  if (existingNode) {
    existingNode.replaceWith(nextNode);
    scrollToBottom(paneMode);
    return;
  }

  appendEventNodeToTimeline(timeline, paneMode, event);
  scrollToBottom(paneMode);
}

export function renderTimeline(mode: BenchmarkMode | PaneMode, events: TimelineEvent[]) {
  const paneMode = resolvePaneMode(mode);
  const timeline = document.getElementById(`timeline-${paneMode}`)!;
  timeline.innerHTML = "";
  for (let i = 0; i < events.length; i++) {
    appendEventNodeToTimeline(timeline, paneMode, events[i], i);
  }
  scrollToBottom(paneMode);
}

function appendEventNodeToTimeline(
  timeline: HTMLElement,
  mode: PaneMode,
  event: TimelineEvent,
  staggerIndex = -1,
) {
  const node = createEventNode(event, mode);
  if (!node) {
    return;
  }
  applyNodeAnimation(node, staggerIndex);

  if (event.isSubagent && event.parentSpawnId) {
    let group = timeline.querySelector(`[data-subagent-group="${event.parentSpawnId}"]`);
    if (!group) {
      const syntheticSpawn: TimelineEvent = {
        id: event.parentSpawnId,
        elapsed: event.elapsed,
        kind: "subagent-spawn",
        actor: event.actor,
        isSubagent: false,
        text: "",
      };
      appendEventNodeToTimeline(timeline, mode, syntheticSpawn);
      group = timeline.querySelector(`[data-subagent-group="${event.parentSpawnId}"]`);
    }
    if (group) {
      insertNodeByElapsed(group as HTMLElement, node, event.elapsed);
      return;
    }
  }

  if (event.kind === "subagent-spawn") {
    const groupWrapper = document.createElement("div");
    groupWrapper.className = "event-node";
    groupWrapper.setAttribute("data-event-id", event.id);
    groupWrapper.setAttribute("data-event-elapsed", String(event.elapsed));
    applyNodeAnimation(groupWrapper, staggerIndex);
    node.removeAttribute("data-event-id");
    node.removeAttribute("data-event-elapsed");
    groupWrapper.appendChild(node);
    const groupBody = document.createElement("div");
    groupBody.className = "subagent-group";
    groupBody.setAttribute("data-subagent-group", event.id);
    if (collapsedSubagentGroups.has(event.id)) {
      groupBody.classList.add("subagent-group--collapsed");
    }
    groupWrapper.appendChild(groupBody);
    timeline.appendChild(groupWrapper);

    const header = groupWrapper.querySelector(
      `[data-subagent-toggle="${event.id}"]`,
    ) as HTMLElement | null;
    const expand = groupWrapper.querySelector(
      `[data-subagent-expand="${event.id}"]`,
    ) as HTMLElement | null;
    const isCollapsed = () => collapsedSubagentGroups.has(event.id);
    const syncCollapsedState = () => {
      groupBody.classList.toggle("subagent-group--collapsed", isCollapsed());
      expand?.classList.toggle("subagent-expand--open", !isCollapsed());
    };
    header?.addEventListener("click", () => {
      if (isCollapsed()) {
        collapsedSubagentGroups.delete(event.id);
      } else {
        collapsedSubagentGroups.add(event.id);
      }
      syncCollapsedState();
    });
    syncCollapsedState();
    insertNodeByElapsed(timeline, groupWrapper, event.elapsed);
    return;
  }

  insertNodeByElapsed(timeline, node, event.elapsed);
}

function insertNodeByElapsed(container: HTMLElement, node: HTMLElement, elapsed: number) {
  const children = Array.from(container.children) as HTMLElement[];
  const insertBefore = children.find((child) => {
    const value = Number(child.getAttribute("data-event-elapsed") ?? Number.NaN);
    return Number.isFinite(value) && value > elapsed;
  });
  if (insertBefore) {
    container.insertBefore(node, insertBefore);
    return;
  }
  container.appendChild(node);
}

function scrollToBottom(mode: PaneMode) {
  const body = document.getElementById(`pane-body-${mode}`)!;
  requestAnimationFrame(() => {
    body.scrollTop = body.scrollHeight;
  });
}

function createEventNode(event: TimelineEvent, mode: PaneMode): HTMLElement | null {
  const wrapper = document.createElement("div");
  wrapper.className = "event-node";
  wrapper.setAttribute("data-event-id", event.id);
  wrapper.setAttribute("data-event-elapsed", String(event.elapsed));

  const timeLabel = formatElapsed(event.elapsed);

  switch (event.kind) {
    case "lifecycle-start":
    case "lifecycle-end":
    case "lifecycle-error": {
      const variant =
        event.kind === "lifecycle-start"
          ? "start"
          : event.kind === "lifecycle-end"
            ? "end"
            : "error";
      const icon =
        event.kind === "lifecycle-start"
          ? iconStart()
          : event.kind === "lifecycle-end"
            ? iconFinish()
            : iconError();
      const label =
        event.kind === "lifecycle-start"
          ? `${shortActor(event.actor)} Agent started`
          : event.kind === "lifecycle-end"
            ? `${shortActor(event.actor)} Agent finished`
            : `${shortActor(event.actor)} Error: ${event.text ?? "unknown"}`;

      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--lifecycle-${variant}"></span>
        <div class="event-lifecycle event-lifecycle--${variant}">
          <span>${icon}</span>
          <span>${escapeHtml(label)}</span>
        </div>
      `;
      return wrapper;
    }

    case "tool-start":
    case "tool-done":
    case "tool-error": {
      const icon = toolIcon(event.toolName);
      const statusClass =
        event.kind === "tool-start" ? "running" : event.kind === "tool-done" ? "done" : "error";
      const statusLabel =
        event.kind === "tool-start" ? "running" : event.kind === "tool-done" ? "done" : "error";
      const durationLabel =
        event.toolDurationMs != null && event.toolDurationMs > 0
          ? formatDuration(event.toolDurationMs)
          : "";
      const dotClass = event.kind === "tool-start" ? "event-dot--tool-running" : "event-dot--tool";
      const nodeId = `tool-${event.id}`;

      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot ${dotClass}"></span>
        <div class="event-tool">
          <div class="event-tool-header" data-toggle="${nodeId}">
            <span class="event-tool-icon">${icon}</span>
            <span class="event-tool-name">${escapeHtml(event.toolName ?? "unknown")}</span>
            <span class="event-tool-detail">${escapeHtml(event.toolDetail ?? "")}</span>
            <span class="event-tool-status event-tool-status--${statusClass}">${statusLabel}</span>
            ${durationLabel ? `<span class="event-tool-duration">${durationLabel}</span>` : ""}
            <span class="event-tool-expand" id="expand-${nodeId}"></span>
          </div>
          <div class="event-tool-body" id="body-${nodeId}">
            ${event.toolArgs ? `<div class="event-tool-body-label">Args <button class="tool-args-toggle" data-args-toggle="${nodeId}" title="Show raw JSON"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></button></div><div id="args-human-${nodeId}">${formatArgsHuman(event.toolArgs)}</div><pre id="args-raw-${nodeId}" class="tool-args-hidden">${escapeHtml(formatJson(event.toolArgs))}</pre>` : ""}
            ${event.toolResult ? `<div class="event-tool-body-label" style="margin-top:8px">Result <button class="tool-args-toggle" data-result-toggle="${nodeId}" title="Show raw JSON"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></button></div><div id="result-human-${nodeId}">${formatArgsHuman(event.toolResult)}</div><pre id="result-raw-${nodeId}" class="tool-args-hidden">${escapeHtml(formatJson(event.toolResult))}</pre>` : ""}
          </div>
        </div>
      `;

      const header = wrapper.querySelector(`[data-toggle="${nodeId}"]`) as HTMLElement;
      header?.addEventListener("click", () => {
        const body = document.getElementById(`body-${nodeId}`);
        const expand = document.getElementById(`expand-${nodeId}`);
        if (body && expand) {
          body.classList.toggle("event-tool-body--open");
          expand.classList.toggle("event-tool-expand--open");
        }
      });

      const argsToggle = wrapper.querySelector(`[data-args-toggle="${nodeId}"]`) as HTMLElement;
      argsToggle?.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleRawView(nodeId, "args", argsToggle);
      });
      const resultToggle = wrapper.querySelector(`[data-result-toggle="${nodeId}"]`) as HTMLElement;
      resultToggle?.addEventListener("click", (e) => {
        e.stopPropagation();
        toggleRawView(nodeId, "result", resultToggle);
      });

      return wrapper;
    }

    case "assistant": {
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--assistant"></span>
        <div class="event-assistant">
          <span class="event-assistant-icon">${iconChat()}</span>
          <span class="event-assistant-preview">${escapeHtml(event.text ?? "")}</span>
        </div>
      `;
      return wrapper;
    }

    case "prompt": {
      const isCollapsed = collapsedPromptEvents.has(event.id);
      const nodeId = `prompt-${event.id}`;
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--assistant"></span>
        <div class="event-prompt">
          <div class="event-prompt-header" data-prompt-toggle="${nodeId}">
            <span class="event-prompt-icon">${iconPrompt()}</span>
            <span class="event-prompt-title">Prompt Sent To ${escapeHtml(shortActor(event.actor))}</span>
            <span class="event-prompt-expand ${isCollapsed ? "" : "event-prompt-expand--open"}" id="expand-${nodeId}"></span>
          </div>
          <div class="event-prompt-body ${isCollapsed ? "" : "event-prompt-body--open"}" id="body-${nodeId}">
            <pre>${escapeHtml(event.text ?? "")}</pre>
          </div>
        </div>
      `;

      const header = wrapper.querySelector(
        `[data-prompt-toggle="${nodeId}"]`,
      ) as HTMLElement | null;
      header?.addEventListener("click", () => {
        const body = document.getElementById(`body-${nodeId}`);
        const expand = document.getElementById(`expand-${nodeId}`);
        if (!body || !expand) {
          return;
        }
        if (collapsedPromptEvents.has(event.id)) {
          collapsedPromptEvents.delete(event.id);
        } else {
          collapsedPromptEvents.add(event.id);
        }
        body.classList.toggle("event-prompt-body--open");
        expand.classList.toggle("event-prompt-expand--open");
      });

      return wrapper;
    }

    case "subagent-spawn": {
      const actorLabel = shortActor(event.actor);
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot" style="background:var(--subagent-accent)"></span>
        <div class="subagent-header" data-subagent-toggle="${event.id}">
          <span>${iconFork()}</span>
          <span>Subagent spawned: ${escapeHtml(actorLabel)}</span>
          <span class="subagent-model">${escapeHtml(event.text ?? "")}</span>
          <span class="subagent-expand" data-subagent-expand="${event.id}"></span>
        </div>
      `;
      return wrapper;
    }

    case "subagent-end": {
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--lifecycle-end"></span>
        <div class="event-lifecycle event-lifecycle--end">
          <span>${iconCheck()}</span>
          <span>Subagent finished: ${escapeHtml(shortActor(event.actor))}</span>
        </div>
      `;
      return wrapper;
    }

    case "validation": {
      const ok = event.validationOk !== false;
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--validation-${ok ? "pass" : "fail"}"></span>
        <div class="event-validation event-validation--${ok ? "pass" : "fail"}">
          <span>${ok ? iconCheck() : iconCross()}</span>
          <span>Validation: ${escapeHtml(event.validationCommand ?? "")}</span>
        </div>
      `;
      return wrapper;
    }

    case "info": {
      wrapper.innerHTML = `
        <span class="event-time">${timeLabel}</span>
        <span class="event-dot event-dot--tool"></span>
        <div class="event-lifecycle event-lifecycle--end">
          <span>${iconInfo()}</span>
          <span>${escapeHtml(event.text ?? "")}</span>
        </div>
      `;
      return wrapper;
    }

    default:
      return null;
  }
}
